import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card, CardBody } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { LoadingState, EmptyState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatUSD } from "@/lib/formatters";
import type { Subscription, SubFrequency } from "@/types";
import { useSubscriptions, useCreateSubscription, useArchiveSubscription } from "./subscriptions.hooks";
import type { SubscriptionInput } from "./subscriptions.adapter";

export function SubscriptionsPage() {
  const { t } = useTranslation();
  const { subscriptions, isLoading, isError, refetch } = useSubscriptions();
  const createSubscription = useCreateSubscription();
  const archiveSubscription = useArchiveSubscription();
  const [toDelete, setToDelete] = useState<Subscription | undefined>();
  const [addOpen, setAddOpen] = useState(false);

  const stats = useMemo(() => {
    const monthly = subscriptions
      .filter((s) => s.status === "ACTIVE")
      .reduce((sum, s) => sum + (s.frequency === "MONTHLY" ? s.cost : s.cost / 12), 0);
    return { monthly, annual: monthly * 12, active: subscriptions.filter((s) => s.status === "ACTIVE").length };
  }, [subscriptions]);

  const onCreate = async (input: SubscriptionInput) => {
    try {
      await createSubscription.mutateAsync(input);
      toast.success("Abonnement ajouté");
      setAddOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'ajout de l'abonnement"));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toDelete) return;
    try {
      await archiveSubscription.mutateAsync(toDelete.id);
      toast.success("Abonnement archivé");
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'archivage"));
    } finally {
      setToDelete(undefined);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("subscriptions.title")}
        subtitle={`${subscriptions.length} abonnements`}
        actions={<Button size="sm" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> {t("common.add")}</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <KPICard label="Coût mensuel (USD)" value={<span className="text-primary">{formatUSD(stats.monthly)}</span>} />
        <KPICard label="Coût annuel (USD)" value={<span className="text-success">{formatUSD(stats.annual)}</span>} />
        <KPICard label="Abonnements actifs" value={stats.active} />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      {isLoading ? (
        <Card><LoadingState rows={4} /></Card>
      ) : subscriptions.length === 0 ? (
        <Card><EmptyState title="Aucun abonnement" message="Ajoutez votre premier abonnement." /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3.5">
          {subscriptions.map((s) => (
            <Card key={s.id}>
              <CardBody>
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary-dim text-primary text-base font-extrabold mb-3">
                  {s.name.charAt(0).toUpperCase()}
                </div>
                <div className="text-[13px] font-bold text-text">{s.name}</div>
                <div className="text-[11px] text-text3 mb-2.5">{s.provider}</div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-text2">{s.frequency === "MONTHLY" ? "Mensuel" : "Annuel"}</span>
                  <span className="mono font-bold">{formatUSD(s.cost)}{s.frequency === "ANNUAL" ? "/an" : ""}</span>
                </div>
                <div className="text-[11px] text-text3 mb-2">Renouvellement: {s.renewalDate}</div>
                <div className="flex items-center gap-1.5">
                  <Badge tone={s.status === "ACTIVE" ? "success" : "default"} dot>{s.status === "ACTIVE" ? "Actif" : "Inactif"}</Badge>
                  <Button variant="ghost" size="xs" className="ml-auto" onClick={() => toast.info("Édition à venir")}><Pencil className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="xs" className="text-danger" onClick={() => setToDelete(s)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <AddSubscriptionModal open={addOpen} onClose={() => setAddOpen(false)} onSave={onCreate} />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title="Archiver l'abonnement"
        message={`Archiver « ${toDelete?.name} » ?`}
        confirmLabel="Archiver"
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}

function AddSubscriptionModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (input: SubscriptionInput) => void }) {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [cost, setCost] = useState("");
  const [frequency, setFrequency] = useState<SubFrequency>("MONTHLY");
  const [renewalDate, setRenewalDate] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    const value = Number(cost);
    if (!name.trim() || !provider.trim() || !value || value <= 0) { setError("Champs invalides"); return; }
    onSave({ name: name.trim(), provider: provider.trim(), cost: value, frequency, renewalDate: renewalDate || undefined });
    setName(""); setProvider(""); setCost(""); setError("");
  };

  return (
    <Modal open={open} onClose={onClose} title="Ajouter un abonnement"
      footer={<><Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button><Button size="sm" onClick={submit}>Ajouter</Button></>}>
      <Field label="Nom de l'outil" required error={error}>
        <input className={fieldInputClass} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Fournisseur" required>
        <input className={fieldInputClass} value={provider} onChange={(e) => setProvider(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Coût USD" required>
          <input type="number" className={fieldInputClass} value={cost} onChange={(e) => setCost(e.target.value)} />
        </Field>
        <Field label="Fréquence" required>
          <select className={fieldInputClass} value={frequency} onChange={(e) => setFrequency(e.target.value as SubFrequency)}>
            <option value="MONTHLY">Mensuel</option>
            <option value="ANNUAL">Annuel</option>
          </select>
        </Field>
      </div>
      <Field label="Renouvellement">
        <input type="date" className={fieldInputClass} value={renewalDate} onChange={(e) => setRenewalDate(e.target.value)} />
      </Field>
    </Modal>
  );
}
